import { extractDateParts, formatDateFull, getDaysInMonth } from './dateFormat'

export type ExpirationStatus = 'none' | 'expired' | 'warning' | 'valid'

export interface ExpirationResult {
  expirationDate: string | null
  formattedDate: string
  status: ExpirationStatus
  daysRemaining: number | null
}

/**
 * Computes the expiration date (signed_date + 6 months) for catalog color/wood/veneer swatches.
 *
 * Rules:
 * - If signedDate is missing or invalid: status is 'none', expirationDate is null, formattedDate is '—'.
 * - Expiration date is exactly 6 months after signedDate, clamping days to the target month (e.g. 2026-03-31 + 6 months -> 2026-09-30).
 * - If expiration date < today (daysRemaining < 0): status is 'expired'.
 * - If 0 <= daysRemaining <= 30: status is 'warning' (expiring soon).
 * - Otherwise: status is 'valid'.
 */
export function computeExpirationDate(
  signedDate: string | null | undefined,
  referenceDate: Date | string = new Date(),
): ExpirationResult {
  const parts = extractDateParts(signedDate)
  if (!parts) {
    return {
      expirationDate: null,
      formattedDate: '—',
      status: 'none',
      daysRemaining: null,
    }
  }

  let targetYear = parts.year
  let targetMonth = parts.month + 6
  if (targetMonth > 12) {
    targetYear += Math.floor((targetMonth - 1) / 12)
    targetMonth = ((targetMonth - 1) % 12) + 1
  }

  const maxDays = getDaysInMonth(targetYear, targetMonth)
  const targetDay = Math.min(parts.day, maxDays)

  const expDateStr = `${String(targetYear).padStart(4, '0')}-${String(targetMonth).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`

  const refParts = extractDateParts(referenceDate)
  let daysRemaining = 0
  if (refParts) {
    const expUtc = Date.UTC(targetYear, targetMonth - 1, targetDay)
    const refUtc = Date.UTC(refParts.year, refParts.month - 1, refParts.day)
    daysRemaining = Math.round((expUtc - refUtc) / (1000 * 60 * 60 * 24))
  }

  let status: ExpirationStatus = 'valid'
  if (daysRemaining < 0) {
    status = 'expired'
  } else if (daysRemaining <= 30) {
    status = 'warning'
  }

  return {
    expirationDate: expDateStr,
    formattedDate: formatDateFull(expDateStr),
    status,
    daysRemaining,
  }
}
